import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import cn from '@/utils/cn';
import { railItemClass } from '../../shared/railClasses';
import SPAButton from '../../shared/SPAButton';
import SPAPageHeader from '../../shared/SPAPageHeader';
import SPASectionHeading from '../../shared/SPASectionHeading';
import { useSPAToast } from '../../shared/SPAToast';
import { useContentMount } from '../contentMount';
import { editSections, programEditPath } from './editSections';
import { PanelSkeleton, hintClass, panelClass } from '../../shared/formPanel';
import {
  NEW_PROGRAM_VALUES, informationPayload, settingsPayload, storeOrderUrlsPayload,
} from './programForm';
import type { ProgramFormValues } from './programForm';
import {
  Divider, IMAGE_LABELS, IMAGES_HINT, ImageRow, UPLOADABLE, emptyImage,
} from './ProgramImageRow';
import ProgramInformationFields from './ProgramInformationFields';
import ProgramSettingsFields from './ProgramSettingsFields';
import StoreOrderUrlsFields from './StoreOrderUrlsFields';
import {
  ProgramImageKind, ProgramImageUploads, updateErrorMessage, useCreateProgram, useLicenses,
} from './useProgram';

// The sections a program has before it is saved, each a panel on this page; the rest of
// editSections need a saved program and open on the edit page afterwards.
const CREATE_SECTIONS = [
  { id: 'new-program-information', label: 'Program Information' },
  { id: 'new-program-settings', label: 'Program Settings' },
  { id: 'new-program-store-urls', label: 'Store Order URLs' },
  { id: 'new-program-images', label: 'Program Images' },
];

const AFTER_SAVE_SECTIONS = editSections.filter(
  section => !CREATE_SECTIONS.some(create => create.label === section.label),
);

interface SectionNavProps {
  active: string;
  onPick: (id: string) => void;
}

// The edit page's section list, as jump links: everything saves together here, so the panels
// stack on one page and the nav scrolls to them.
const NewProgramSectionNav = ({ active, onPick }: SectionNavProps) => (
  <nav
    aria-label="New program sections"
    className="tw-flex tw-w-56 tw-shrink-0 tw-flex-col tw-gap-0.5 md:tw-sticky md:tw-top-4"
  >
    {CREATE_SECTIONS.map(section => (
      <a
        key={section.id}
        aria-current={active === section.id ? 'location' : undefined}
        className={railItemClass(active === section.id)}
        href={`#${section.id}`}
        onClick={() => onPick(section.id)}
      >
        {section.label}
      </a>
    ))}
    <span className={cn(hintClass, 'tw-px-2 tw-pt-3')}>Available once the program is saved</span>
    {AFTER_SAVE_SECTIONS.map(section => (
      <span
        key={section.label}
        aria-disabled="true"
        className={cn(railItemClass(false), 'tw-text-spa-tertiary hover:tw-bg-transparent')}
      >
        {section.label}
      </span>
    ))}
  </nav>
);

const sectionClass = cn(panelClass, 'tw-scroll-mt-4');

// The submit row stays in view like the legacy form's fixed bar; it sits on the page ground so the
// panels scroll under it.
const footerClass = cn(
  'tw-sticky tw-bottom-0 tw-z-10 tw--mx-6 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2',
  'tw-border-0 tw-border-t tw-border-solid tw-border-spa-border tw-bg-spa-bg tw-px-6 tw-py-3',
);

const NewProgramPage = () => {
  const navigate = useNavigate();
  const mount = useContentMount();
  const spaToast = useSPAToast();
  const licenses = useLicenses();
  const createProgram = useCreateProgram();
  const form = useForm<ProgramFormValues>({ defaultValues: NEW_PROGRAM_VALUES });
  const [uploads, setUploads] = useState<ProgramImageUploads>({});
  const [activeSection, setActiveSection] = useState(CREATE_SECTIONS[0].id);

  const pickImage = (kind: ProgramImageKind) => (file: File | undefined) => (
    setUploads(current => ({ ...current, [kind]: file }))
  );

  const create = (values: ProgramFormValues) => {
    const program = {
      ...informationPayload(values),
      ...settingsPayload(values, false),
      ...storeOrderUrlsPayload(values),
    };

    createProgram.mutate({ program, uploads }, {
      onSuccess: (created) => {
        spaToast.success(`Created “${created.title}”.`);
        navigate(programEditPath(created.id, mount));
      },
    });
  };

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <SPAPageHeader title="New Program" />
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-6 md:tw-flex-row">
          <NewProgramSectionNav active={activeSection} onPick={setActiveSection} />
          <div className="tw-min-w-0 tw-flex-1">
            {licenses.isLoading && <PanelSkeleton />}
            {licenses.isError && (
              <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
                Couldn’t load the licenses. Reload the page to try again.
              </p>
            )}
            {licenses.data && (
              <form noValidate className="tw-flex tw-flex-col tw-gap-6" onSubmit={form.handleSubmit(create)}>
                <section className={sectionClass} id={CREATE_SECTIONS[0].id}>
                  <SPASectionHeading className="tw-mb-5" title="Program Information" />
                  <ProgramInformationFields form={form} licenses={licenses.data} />
                </section>
                <section className={sectionClass} id={CREATE_SECTIONS[1].id}>
                  <SPASectionHeading className="tw-mb-5" title="Program Settings" />
                  <ProgramSettingsFields custom={false} form={form} />
                </section>
                <section className={sectionClass} id={CREATE_SECTIONS[2].id}>
                  <SPASectionHeading className="tw-mb-5" title="Store Order URLs" />
                  <StoreOrderUrlsFields form={form} />
                </section>
                <section className={sectionClass} id={CREATE_SECTIONS[3].id}>
                  <SPASectionHeading className="tw-mb-3" title="Program Images" />
                  <span className={cn(hintClass, 'tw-mb-3 tw-mt-0')}>{IMAGES_HINT}</span>
                  <div className="tw-flex tw-flex-col">
                    {UPLOADABLE.map((kind, index) => (
                      <React.Fragment key={kind}>
                        {index > 0 && <Divider />}
                        <ImageRow
                          image={emptyImage(kind)}
                          inputId={`program_${kind}`}
                          label={IMAGE_LABELS[kind]}
                          pending={uploads[kind]}
                          onPick={pickImage(kind)}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </section>

                <div className={footerClass}>
                  {createProgram.isError && (
                    <p className="tw-m-0 tw-mr-auto tw-text-sm tw-text-spa-error" role="alert">
                      {updateErrorMessage(createProgram.error)}
                    </p>
                  )}
                  <SPAButton asChild>
                    <Link to={mount === 'legacy' ? '/' : '/content'}>Cancel</Link>
                  </SPAButton>
                  <SPAButton disabled={createProgram.isLoading} type="submit" variant="primary">
                    {createProgram.isLoading ? 'Creating…' : 'Create Program'}
                  </SPAButton>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewProgramPage;
